import { ApiClient } from 'ApiClient';

// 1 = Street Food, 2 = To Go (matches ShopTypes constants on the API side)
export type LightspeedShopId = 1 | 2;
export type LightspeedPayloadType = 'items' | 'production_instructions';

// Response shapes: ApiClient runs camelcaseKeys(deep) on every response,
// so what the controller emits as `shop_id` becomes `shopId` here, etc.

export interface LightspeedSnapshotMeta {
  id: number;
  shopId: number;
  payloadType: LightspeedPayloadType;
  parsedCount: number;
  pastedAt: string;
}

export interface LightspeedCatalogItem {
  sku?: string;
  name?: string;
  [k: string]: unknown;
}

export interface LightspeedProductionInstruction {
  instruction: string;
  // camelcaseKeys leaves Lightspeed's already-camelCase field untouched.
  ikentooModifierId: number | string;
}

export interface LightspeedProductionInstructionGroup {
  productionInstructionGroupName: string;
  productionIntructionGroupId: number | string; // sic — Lightspeed's typo
  multiSelectionPermitted: boolean;
  productionInstructionList: LightspeedProductionInstruction[];
}

export interface LightspeedSnapshotsForShop {
  shopId: number;
  itemsSnapshot:
    | (Omit<LightspeedSnapshotMeta, 'payloadType'> & {
        parsed: LightspeedCatalogItem[];
      })
    | null;
  productionInstructionsSnapshot:
    | (Omit<LightspeedSnapshotMeta, 'payloadType'> & {
        parsed: LightspeedProductionInstructionGroup[];
      })
    | null;
}

export interface LightspeedStateMenuItem {
  id: number;
  title: string;
  price: string | number;
  lightspeedSku: string | null;
  lightspeedModifierId: string | null;
  menuCategoryId: number;
  categoryName: string;
  sectionName: string;
}

export interface LightspeedStateSetMenu {
  id: number;
  name: string;
  price: string | number;
  lightspeedSku: string | null;
}

export interface LightspeedShopState {
  shopId: number;
  menuItems: LightspeedStateMenuItem[];
  setMenus: LightspeedStateSetMenu[];
}

class LightspeedMappingService {
  /**
   * POST /lightspeed-mapping/pull-modifiers/{shopId}
   * Server-side fetch of the production-instructions catalog from
   * Lightspeed (GET /peking/modifiers, Basic auth). No paste needed.
   */
  public pullModifiers(shopId: LightspeedShopId) {
    return ApiClient.post<unknown, LightspeedSnapshotMeta>(
      `lightspeed-mapping/pull-modifiers/${shopId}`,
      {},
    );
  }

  /** POST /lightspeed-mapping/snapshots */
  public addSnapshot(args: {
    shopId: LightspeedShopId;
    payloadType: LightspeedPayloadType;
    rawPayload: string;
    notes?: string;
  }) {
    return ApiClient.post<unknown, LightspeedSnapshotMeta>(
      'lightspeed-mapping/snapshots',
      {
        shop_id: args.shopId,
        payload_type: args.payloadType,
        raw_payload: args.rawPayload,
        notes: args.notes ?? '',
      },
    );
  }

  /** GET /lightspeed-mapping/snapshots/{shopId} */
  public getSnapshots(shopId: LightspeedShopId) {
    return ApiClient.get<LightspeedSnapshotsForShop>(
      `lightspeed-mapping/snapshots/${shopId}`,
    );
  }

  /** GET /lightspeed-mapping/state/{shopId} */
  public getState(shopId: LightspeedShopId) {
    return ApiClient.get<LightspeedShopState>(
      `lightspeed-mapping/state/${shopId}`,
    );
  }

  /** PUT /lightspeed-mapping/menu-items/{id} */
  public updateMenuItem(
    id: number,
    args: {
      shopId: LightspeedShopId;
      lightspeed_sku?: string | null;
      lightspeed_modifier_id?: string | null;
    },
  ) {
    const body: Record<string, unknown> = { shop_id: args.shopId };
    if (args.lightspeed_sku !== undefined) {
      body.lightspeed_sku = args.lightspeed_sku ?? '';
    }
    if (args.lightspeed_modifier_id !== undefined) {
      body.lightspeed_modifier_id = args.lightspeed_modifier_id ?? '';
    }
    return ApiClient.put<
      unknown,
      {
        id: number;
        lightspeedSku: string | null;
        lightspeedModifierId: string | null;
      }
    >(`lightspeed-mapping/menu-items/${id}`, body);
  }

  /** PUT /lightspeed-mapping/set-menus/{id} */
  public updateSetMenu(
    id: number,
    args: {
      shopId: LightspeedShopId;
      lightspeed_sku: string | null;
    },
  ) {
    return ApiClient.put<
      unknown,
      { id: number; lightspeedSku: string | null }
    >(`lightspeed-mapping/set-menus/${id}`, {
      shop_id: args.shopId,
      lightspeed_sku: args.lightspeed_sku ?? '',
    });
  }
}

const singleton = new LightspeedMappingService();
export { singleton as LightspeedMappingService };
